import type { ScrollAxis } from "./scroll-box-axes"
import { ownTextRects } from "./scroll-box-content-leaves"
import { MAXIMUM_ELEMENTS_READ_PER_WALK, childrenNearView, extentNearViewPx, nearViewSpan } from "./scroll-box-near-view"
import type { PixelSpan } from "./scroll-box-spans"
import {
  hasLaidOutBox,
  hasNoBoxOfItsOwn,
  hasOwnText,
  hidesOwnContentAlong,
  isInlineLevel,
  isRendered,
  isStickyPositioned,
  isVisibilityHidden,
  layoutRect,
  lineHeightPx,
} from "./scroll-box-styles"

export interface ContentUnit {
  startPx: number
  endPx: number
  crossStartPx: number
  crossEndPx: number
}

interface LineSpan {
  startPx: number
  endPx: number
}

interface LineWalk {
  axis: ScrollAxis
  effectivePx: number
  nearView: PixelSpan
  spans: LineSpan[]
  elementsRead: number
}

const MAXIMUM_ITEM_SEARCH_DEPTH = 8
const MINIMUM_REPEATED_SIBLINGS = 2
const MINIMUM_SAME_TAG_SIBLINGS = 3
const NESTED_ITEMS_DOMINANT_SHARE = 0.5
const ADVANCE_TOLERANCE_PX = 0.5
const SAME_LINE_TOLERANCE_PX = 1
const MULTILINE_TEXT_BLOCK_FACTOR = 1.5
const MINIMUM_LINE_TO_LINE_HEIGHT_RATIO = 0.5

function isLaidOutBlock(element: Element): boolean {
  if (hasNoBoxOfItsOwn(element)) return blockChildren(element).length > 0
  return (
    isRendered(element) &&
    !isVisibilityHidden(element) &&
    !isStickyPositioned(element) &&
    !isInlineLevel(element) &&
    hasLaidOutBox(element)
  )
}

function blockChildren(element: Element): Element[] {
  return Array.from(element.children).filter(isLaidOutBlock)
}

function kindSignature(element: Element): string {
  return `${element.tagName}.${element.classList.item(0) ?? ""}`
}

function childKindsSignature(element: Element): string {
  return [...new Set(blockChildren(element).map(kindSignature))].sort().join(",")
}

function itemSignature(element: Element): string {
  return `${kindSignature(element)}[${childKindsSignature(element)}]`
}

function sameTagSignature(element: Element): string {
  return `${element.tagName}[${childKindsSignature(element)}]`
}

function occurrenceCounts(keys: readonly string[]): number[] {
  const counts = new Map<string, number>()
  for (const key of keys) counts.set(key, (counts.get(key) ?? 0) + 1)
  return keys.map((key) => counts.get(key) ?? 0)
}

function elementsSharingKey(
  elements: Element[],
  keyOf: (element: Element) => string,
  minimumOccurrencesOf: (element: Element) => number,
): Element[] {
  const counts = occurrenceCounts(elements.map(keyOf))
  return elements.filter((element, index) => counts[index] >= minimumOccurrencesOf(element))
}

function sharesOnlyItsTag(element: Element): boolean {
  return element.classList.length === 0 && blockChildren(element).length === 0
}

function minimumRepeatsOfItemSignature(element: Element): number {
  return sharesOnlyItsTag(element) ? MINIMUM_SAME_TAG_SIBLINGS : MINIMUM_REPEATED_SIBLINGS
}

function minimumRepeatsOfSameTag(): number {
  return MINIMUM_SAME_TAG_SIBLINGS
}

function blockChildrenNearView(parent: Element, axis: ScrollAxis, nearView: PixelSpan): Element[] {
  return childrenNearView(parent, axis, nearView).filter(isLaidOutBlock)
}

function repeatingSiblings(parent: Element, axis: ScrollAxis, nearView: PixelSpan): Element[] {
  const children = blockChildrenNearView(parent, axis, nearView)
  if (children.length < MINIMUM_REPEATED_SIBLINGS) return []
  const sameSignature = elementsSharingKey(children, itemSignature, minimumRepeatsOfItemSignature)
  if (sameSignature.length >= MINIMUM_REPEATED_SIBLINGS) return sameSignature
  if (children.length < MINIMUM_SAME_TAG_SIBLINGS) return []
  return elementsSharingKey(children, sameTagSignature, minimumRepeatsOfSameTag)
}

function advancesAlong(items: Element[], axis: ScrollAxis): boolean {
  const firstEndPx = layoutRect(items[0])[axis.rectEnd]
  return items.slice(1).some((item) => layoutRect(item)[axis.rectStart] >= firstEndPx - ADVANCE_TOLERANCE_PX)
}

function advancingRepeatingSiblings(parent: Element, axis: ScrollAxis, nearView: PixelSpan): Element[] {
  const siblings = repeatingSiblings(parent, axis, nearView)
  return siblings.length > 0 && advancesAlong(siblings, axis) ? siblings : []
}

function extentSumPx(elements: Element[], axis: ScrollAxis, nearView: PixelSpan): number {
  return elements.reduce((total, element) => total + extentNearViewPx(element, axis, nearView), 0)
}

function stacksAlong(elements: Element[], axis: ScrollAxis): boolean {
  const rects = elements.map(layoutRect)
  return rects.every(
    (rect, index) => index === 0 || rect[axis.rectStart] >= rects[index - 1][axis.rectEnd] - ADVANCE_TOLERANCE_PX,
  )
}

function showsListMarker(element: Element): boolean {
  const style = getComputedStyle(element)
  return style.display === "list-item" && style.listStyleType !== "none"
}

function isTextBlock(element: Element): boolean {
  return element.tagName === "P" || showsListMarker(element)
}

function childrenGroupedByKind(items: Element[], axis: ScrollAxis, nearView: PixelSpan): Element[][] {
  const groups = new Map<string, Element[]>()
  const children = items.flatMap((item) => blockChildrenNearView(item, axis, nearView))
  for (const child of children.filter((candidate) => !isTextBlock(candidate))) {
    const kind = kindSignature(child)
    groups.set(kind, [...(groups.get(kind) ?? []), child])
  }
  return [...groups.values()]
}

function dominantStackedChildren(items: Element[], axis: ScrollAxis, nearView: PixelSpan): Element[] {
  if (items.some((item) => hidesOwnContentAlong(item, axis))) return []
  const stackedKinds = childrenGroupedByKind(items, axis, nearView).filter(
    (children) => children.length >= MINIMUM_REPEATED_SIBLINGS && stacksAlong(children, axis),
  )
  const widestKind = stackedKinds.reduce<Element[]>(
    (widest, children) =>
      extentSumPx(children, axis, nearView) > extentSumPx(widest, axis, nearView) ? children : widest,
    [],
  )
  const itemsExtentPx = extentSumPx(items, axis, nearView)
  return extentSumPx(widestKind, axis, nearView) >= itemsExtentPx * NESTED_ITEMS_DOMINANT_SHARE ? widestKind : []
}

function refineToInnermostItems(items: Element[], axis: ScrollAxis, nearView: PixelSpan): Element[] {
  const nested = dominantStackedChildren(items, axis, nearView)
  if (nested.length < items.length) return items
  if (nested.length > items.length) return refineToInnermostItems(nested, axis, nearView)
  const throughWrappers = refineToInnermostItems(nested, axis, nearView)
  return throughWrappers.length > nested.length ? throughWrappers : items
}

function isSameComponentInAnotherState(element: Element, item: Element): boolean {
  const classes = element.getAttribute("class") ?? ""
  return element.tagName === item.tagName && classes !== "" && classes === item.getAttribute("class")
}

function itemsOfComponentsInAnotherState(
  parent: Element,
  items: Element[],
  axis: ScrollAxis,
  nearView: PixelSpan,
): Element[] {
  return blockChildrenNearView(parent, axis, nearView)
    .filter((child) => !items.includes(child) && isSameComponentInAnotherState(child, items[0]))
    .flatMap((child) => {
      const nestedItems = findRepeatingItemsNearView(child, axis, nearView)
      return nestedItems.length > 0 ? nestedItems : [child]
    })
}

function inDocumentOrder(elements: Element[]): Element[] {
  return [...elements].sort((first, second) =>
    first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
  )
}

function findRepeatingItemsNearView(box: Element, axis: ScrollAxis, nearView: PixelSpan): Element[] {
  let level: Element[] = [box]
  for (let depth = 0; depth < MAXIMUM_ITEM_SEARCH_DEPTH && level.length > 0; depth++) {
    const nextLevel: Element[] = []
    for (const parent of level) {
      const items = advancingRepeatingSiblings(parent, axis, nearView)
      if (items.length > 0) {
        return inDocumentOrder([
          ...refineToInnermostItems(items, axis, nearView),
          ...itemsOfComponentsInAnotherState(parent, items, axis, nearView),
        ])
      }
      nextLevel.push(...blockChildrenNearView(parent, axis, nearView).filter((child) => !hidesOwnContentAlong(child, axis)))
    }
    level = nextLevel
  }
  return []
}

export function findRepeatingItems(box: Element, axis: ScrollAxis): Element[] {
  return findRepeatingItemsNearView(box, axis, nearViewSpan(box, axis))
}

export function itemContentUnits(items: Element[], axis: ScrollAxis): ContentUnit[] {
  return items.map((item) => {
    const rect = layoutRect(item)
    return {
      startPx: rect[axis.rectStart],
      endPx: rect[axis.rectEnd],
      crossStartPx: rect[axis.crossRectStart],
      crossEndPx: rect[axis.crossRectEnd],
    }
  })
}

function spanAlong(rect: DOMRect, axis: ScrollAxis): LineSpan {
  return { startPx: rect[axis.rectStart], endPx: rect[axis.rectEnd] }
}

function textLineRects(element: Element): DOMRect[] {
  const range = document.createRange()
  range.selectNodeContents(element)
  return Array.from(range.getClientRects()).filter((rect) => rect.width > 0 && rect.height > 0)
}

function mergeSpansSharingStart(spans: readonly LineSpan[]): LineSpan[] {
  const merged: LineSpan[] = []
  for (const span of [...spans].sort((left, right) => left.startPx - right.startPx)) {
    const previous = merged[merged.length - 1]
    if (previous && Math.abs(span.startPx - previous.startPx) <= SAME_LINE_TOLERANCE_PX) {
      previous.endPx = Math.max(previous.endPx, span.endPx)
    } else {
      merged.push({ ...span })
    }
  }
  return merged
}

function isLineLeaf(element: Element, visibleChildren: Element[], axis: ScrollAxis): boolean {
  return (
    hasOwnText(element) ||
    visibleChildren.length === 0 ||
    visibleChildren.every(isInlineLevel) ||
    hidesOwnContentAlong(element, axis)
  )
}

function wrappedTextLines(element: Element, axis: ScrollAxis): LineSpan[] {
  const isMultilineTextBlock =
    axis.name === "vertical" &&
    element.getBoundingClientRect().height > lineHeightPx(element) * MULTILINE_TEXT_BLOCK_FACTOR
  return isMultilineTextBlock ? mergeSpansSharingStart(textLineRects(element).map((rect) => spanAlong(rect, axis))) : []
}

function collectLineSpans(element: Element, walk: LineWalk): void {
  if (walk.elementsRead > MAXIMUM_ELEMENTS_READ_PER_WALK) return
  if (!isRendered(element) || isStickyPositioned(element) || element instanceof HTMLTextAreaElement) return
  walk.elementsRead++
  const visibleChildren = Array.from(element.children).filter(isRendered)
  if (!isLineLeaf(element, visibleChildren, walk.axis)) {
    for (const child of childrenNearView(element, walk.axis, walk.nearView)) collectLineSpans(child, walk)
    return
  }
  if (!hasLaidOutBox(element)) return
  const rect = element.getBoundingClientRect()
  const hasText = (element.textContent ?? "").trim().length > 0
  if (!hasText && rect[walk.axis.rectExtent] > walk.effectivePx) return
  const lines = hasText ? wrappedTextLines(element, walk.axis) : []
  if (lines.length > 1) walk.spans.push(...lines)
  else walk.spans.push(spanAlong(rect, walk.axis))
}

export function findTextLines(box: Element, axis: ScrollAxis, effectivePx: number): ContentUnit[] {
  const walk: LineWalk = {
    axis,
    effectivePx,
    nearView: nearViewSpan(box, axis),
    spans: ownTextRects(box).map((rect) => spanAlong(rect, axis)),
    elementsRead: 0,
  }
  for (const child of childrenNearView(box, axis, walk.nearView)) collectLineSpans(child, walk)
  if (walk.elementsRead > MAXIMUM_ELEMENTS_READ_PER_WALK) return []
  const spans = walk.spans
  const minimumLinePx = lineHeightPx(box) * MINIMUM_LINE_TO_LINE_HEIGHT_RATIO
  return mergeSpansSharingStart(spans.filter((span) => span.endPx - span.startPx >= minimumLinePx)).map((span) => ({
    ...span,
    crossStartPx: 0,
    crossEndPx: 0,
  }))
}
